'use client';

import { c as _c } from "react/compiler-runtime";
import { Table } from '@/components/common/Table';
import * as Compact from '@/components/common/Table/CompactTable/Table';
import type { FeedRow } from './oracleFeeds';
import { type FeedBranchGroup, type FeedSideGroup, isEmptySlot } from './groupBySide';
import { AdminValue, FeedName, FeedValue, Muted, ProviderLabel, SideLabel, TreeConnector, TypeBadge, UpdatesValue } from './OracleFeedParts';

/** Fixed widths, so stacked tables (Feed 1 / Feed 2, every oracle on a page) line up. */
const COLUMNS = [{
  id: 'feed',
  label: 'Feed',
  width: '22%'
}, {
  id: 'type',
  label: 'Type',
  width: '12%'
}, {
  id: 'provider',
  label: 'Provider',
  width: '15%'
}, {
  id: 'admin',
  label: 'Admin',
  width: '20%'
}, {
  id: 'updates',
  label: 'Heartbeat / Deviation',
  width: '18%'
}, {
  id: 'value',
  label: 'Latest price',
  width: '13%'
}] as const;
function GroupRow(t0) {
  const $ = _c(2);
  const {
    group
  } = t0;
  let t1;
  if ($[0] !== group) {
    t1 = <Table.Tr><Compact.Td colSpan={COLUMNS.length} className="[&_.td-content]:h-[28px]"><div className="text-3xs text-secondary-foreground">{group ? <SideLabel group={group} /> : <span className="text-muted-foreground">Oracle</span>}</div></Compact.Td></Table.Tr>;
    $[0] = group;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function FeedTableRow(t0) {
  const $ = _c(34);
  const {
    chainId,
    row,
    sub
  } = t0;
  let t1;
  if ($[0] !== row) {
    t1 = isEmptySlot(row);
    $[0] = row;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const emptySlot = t1;
  const t2 = sub ? 24 : 0;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      paddingLeft: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== sub) {
    t4 = sub && <TreeConnector last={sub.last} className="left-xs w-2.5" />;
    $[4] = sub;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== chainId || $[7] !== row) {
    t5 = <FeedName chainId={chainId} row={row} />;
    $[6] = chainId;
    $[7] = row;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <Compact.Td><span className="gap-xs -my-xs py-xs relative flex min-w-0 items-center self-stretch" style={t3}>{t4}{t5}</span></Compact.Td>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== row.providerType || $[14] !== row.type) {
    t7 = <Compact.Td><TypeBadge type={row.type} providerType={row.providerType} /></Compact.Td>;
    $[13] = row.providerType;
    $[14] = row.type;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== emptySlot || $[17] !== row) {
    t8 = <Compact.Td>{emptySlot ? <Muted /> : <ProviderLabel feed={row} />}</Compact.Td>;
    $[16] = emptySlot;
    $[17] = row;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== chainId || $[20] !== emptySlot || $[21] !== row.admins) {
    t9 = <Compact.Td>{emptySlot ? <Muted /> : <AdminValue chainId={chainId} admins={row.admins} />}</Compact.Td>;
    $[19] = chainId;
    $[20] = emptySlot;
    $[21] = row.admins;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== row) {
    t10 = <Compact.Td><UpdatesValue row={row} /></Compact.Td>;
    $[23] = row;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== row) {
    t11 = <Compact.Td><FeedValue row={row} /></Compact.Td>;
    $[25] = row;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t12 = <Table.Tr className="text-2xs">{t6}{t7}{t8}{t9}{t10}{t11}</Table.Tr>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}

/** One table per oracle branch; rows grouped Collateral / Loan, each feed's direct children indented under it. */
export function OracleFeedsTable(t0) {
  const $ = _c(6);
  const {
    chainId,
    branch
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <colgroup>{COLUMNS.map(_temp)}</colgroup>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Compact.Thead><Table.Tr>{COLUMNS.map(_temp2)}</Table.Tr></Compact.Thead>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== branch.oracleRows || $[3] !== branch.sides || $[4] !== chainId) {
    t3 = <Compact.TableScrollArea className="grid w-full"><Compact.Table tableVariant="compact" className="min-w-[760px] table-fixed [&_.td-content]:pr-[16px] [&_th]:pr-[16px]">{t1}{t2}<Compact.Tbody tableVariant="compact">{[...(branch.oracleRows.length ? [{
            key: "oracle",
            group: null,
            rows: branch.oracleRows
          }] : []), ...branch.sides.map(_temp3)].map(t4 => {
            const {
              key,
              group: group_0,
              rows
            } = t4;
            return [<GroupRow key={key} group={group_0} />, ...rows.flatMap(row => [<FeedTableRow key={row.id} chainId={chainId} row={row} />, ...row.children.map((child, index) => <FeedTableRow key={child.id} chainId={chainId} row={child} sub={{
              last: index === row.children.length - 1
            }} />)])];
          })}</Compact.Tbody></Compact.Table></Compact.TableScrollArea>;
    $[2] = branch.oracleRows;
    $[3] = branch.sides;
    $[4] = chainId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp3(group) {
  return {
    key: group.side,
    group,
    rows: group.rows
  };
}
function _temp2(column_0) {
  return <Compact.Th key={column_0.id} tableVariant="compact">{column_0.label}</Compact.Th>;
}
function _temp(column) {
  return <col key={column.id} style={{
    width: column.width
  }} />;
}